import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { getOdcDefaultData, filterOdcOrders } from '../utilities/api';
import OdcListHeader from './OdcListHeader';
import OdcListContent from './OdcListContent';

const OdcOrdersPage = ({ role }) => {
    const abortControllerRef = useRef(null);

    const userRole = role || localStorage.getItem('role') || '';
    const clientId = localStorage.getItem('clientid');

    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isBackgroundSyncing, setIsBackgroundSyncing] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [options, setOptions] = useState({ branches: [], companies: [] });
    const [filters, setFilters] = useState({
        dates: null,
        selectedBranch: null,
        selectedStatus: 'pending',
        selectedCompany: null
    });

    const statusOptions = [
        { label: 'Pending', value: 'pending' },
        ...(userRole !== 'CEO' ? [{ label: 'Dispatched', value: 'dispatched' }] : []),
        ...(userRole !== 'MANAGER'
            ? [
                  { label: 'Payment Pending', value: 'dispatched' },
                  { label: 'Dispatched Only', value: 'dispatchedonly' },
                  { label: 'Settled', value: 'settled' },
                  { label: 'Cancelled', value: 'cancelled' }
              ]
            : [])
    ];

    const loadInitialData = useCallback(async () => {
        try {
            const data = await getOdcDefaultData({ rolename: userRole, clientname: clientId, branch_id: localStorage.getItem('branchid') });
            setOptions({ branches: data.branchs || [], companies: data.company || [] });
            if (data.bills && data.bills.length > 0) {
                setTableData(data.bills);
                setTotalRecords(data.bills.length);
            }
        } catch (e) {
            console.error('loadInitialData error:', e);
        }
    }, [userRole, clientId]);

    const fetchFilteredData = useCallback(async () => {
        if (filters.dates && filters.dates[0] && !filters.dates[1]) return;

        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setLoading(true);
        setIsBackgroundSyncing(false);
        setTableData([]);
        setTotalRecords(0);

        const BATCH_SIZE = 100;

        const loadNextBatch = async (offset = 0) => {
            try {
                if (offset > 0) setIsBackgroundSyncing(true);

                const sessionBranchId = localStorage.getItem('branchid');
                const finalBranchId = userRole === 'CEO' ? filters.selectedBranch || '' : sessionBranchId || '';

                const data = await filterOdcOrders(filters.selectedStatus, {
                    rolename: userRole,
                    clientname: clientId,
                    from_date: filters.dates?.[0] ? moment(filters.dates[0]).format('YYYY-MM-DD') : '',
                    to_date: filters.dates?.[1] ? moment(filters.dates[1]).format('YYYY-MM-DD') : '',
                    branch_id: finalBranchId,
                    status: filters.selectedStatus || '',
                    company_id: filters.selectedCompany || '',
                    limit: BATCH_SIZE,
                    offset
                }, signal);

                const newItems = data.bills || [];

                if (data.total_count) setTotalRecords(parseInt(data.total_count));

                if (newItems.length > 0) {
                    setTableData((prev) => [...prev, ...newItems]);
                    if (offset === 0) setLoading(false);
                    if (newItems.length === BATCH_SIZE) loadNextBatch(offset + BATCH_SIZE);
                    else setIsBackgroundSyncing(false);
                } else {
                    setLoading(false);
                    setIsBackgroundSyncing(false);
                }
            } catch (err) {
                if (err.name === 'AbortError' || err.name === 'CanceledError') return;
                setLoading(false);
                setIsBackgroundSyncing(false);
            }
        };

        loadNextBatch(0);
    }, [filters, userRole, clientId]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);
    useEffect(() => {
        fetchFilteredData();
    }, [fetchFilteredData]);

    const handleReset = () =>
        setFilters({
            dates: null,
            selectedBranch: null,
            selectedStatus: 'pending',
            selectedCompany: null
        });

    return (
        <div className="p-3">
            <OdcListHeader filters={filters} setFilters={setFilters} options={{ ...options, statusOptions }} loading={loading} onReset={handleReset} />
            <OdcListContent
                tableData={tableData}
                loading={loading}
                isBackgroundSyncing={isBackgroundSyncing}
                totalRecords={totalRecords}
                filters={filters}
                options={options}
                userRole={userRole}
                fetchFilteredData={fetchFilteredData}
            />
        </div>
    );
};

export default OdcOrdersPage;
